import React from "react";
import { rawColors } from "../../../design-system/tokens/colors";
import type { SlideThumbnailPreview } from "./SlideThumbnail.types";

/**
 * Skeleton preview graphics for the present-mode slide thumbnails.
 *
 * Implemented as SVGs (not styled divs) because the Figma micro-geometry
 * (6px lines, 9px bars, percentage offsets) does not sit on the 4px spacing
 * grid — SVG coordinates keep the artwork exact without hardcoding CSS
 * spacing. All fills come from design tokens.
 */

const grey300 = rawColors.grey[300];
const grey500 = rawColors.grey[500];
const primary100 = rawColors.primary[100];
const primary200 = rawColors.primary[200];
const trendOrange = rawColors.accent.peach;
const trendPurple = rawColors.brand.primary;

const svgProps = (testId: string) =>
  ({
    width: "100%",
    height: "100%",
    preserveAspectRatio: "none",
    fill: "none",
    role: "presentation",
    "data-testid": testId,
  }) as const;

const TextPreview: React.FC = () => (
  <svg viewBox="0 0 104 52" {...svgProps("slide-preview-text")}>
    <rect width="104" height="52" rx="4" fill={grey300} opacity="0.8" />
    <rect x="11.2" y="11" width="83.1" height="6" rx="2" fill={grey500} opacity="0.4" />
    <rect x="11.2" y="21" width="83.1" height="6" rx="2" fill={grey500} opacity="0.4" />
    <rect x="11.2" y="32" width="55.9" height="6" rx="2" fill={grey500} opacity="0.4" />
  </svg>
);

/** One KPI stat box: grey card + label line (primary100) + value line (primary200). */
const KpiBox: React.FC<{ x: number }> = ({ x }) => (
  <g transform={`translate(${x} 9)`}>
    <rect width="32.7" height="34" rx="4" fill={grey300} opacity="0.8" />
    <rect x="3.6" y="7" width="19.6" height="4" rx="2" fill={primary100} />
    <rect x="3.6" y="21" width="11.6" height="10" rx="2" fill={primary200} opacity="0.6" />
  </g>
);

const KpiPreview: React.FC = () => (
  <svg viewBox="0 0 104 52" {...svgProps("slide-preview-kpi")}>
    <KpiBox x={0} />
    <KpiBox x={35.65} />
    <KpiBox x={71.3} />
  </svg>
);

const BAR_CHART_ROWS = [
  { y: 0, width: 104 },
  { y: 10.73, width: 90.5 },
  { y: 21.46, width: 68.4 },
  { y: 32.19, width: 36.7 },
  { y: 42.92, width: 22.9 },
];

const BarChartPreview: React.FC = () => (
  <svg viewBox="0 0 104 52" {...svgProps("slide-preview-barChart")}>
    {BAR_CHART_ROWS.map(({ y, width }) => (
      <rect key={y} y={y} width={width} height="9.08" rx="1" fill={primary200} />
    ))}
  </svg>
);

/** Column x-offsets and heights traced from the Figma "Performance" slide. */
const TREND_COLUMNS = [
  { x: 0, y: 33.9 },
  { x: 17.9, y: 31.3 },
  { x: 35.8, y: 26.7 },
  { x: 53.7, y: 21.0 },
  { x: 71.6, y: 13.2 },
  { x: 89.5, y: 11.2 },
];

const TrendChartPreview: React.FC = () => (
  <svg viewBox="0 0 104 52" {...svgProps("slide-preview-trendChart")}>
    {TREND_COLUMNS.map(({ x, y }) => (
      <path
        key={x}
        d={`M${x} ${y + 1}q0-1 1-1h12.5q1 0 1 1V52H${x}Z`}
        fill={primary200}
      />
    ))}
    <path
      d="M7.9 0.6 L25.7 6.1 L45.4 7.2 L63.8 10.9 L83.4 12.4 L102.6 10.9"
      stroke={trendOrange}
    />
    <path
      d="M7.6 35.6 L26.5 33.2 L45.4 28.9 L64.3 24 L84 16 L102.6 13.4"
      stroke={trendPurple}
    />
  </svg>
);

/** One recommended-action tile: grey card + three short text lines. */
const ActionBox: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width="47" height="24.6" rx="4" fill={grey300} opacity="0.8" />
    <rect x="5.2" y="5.1" width="36.6" height="2.9" rx="1.4" fill={grey500} opacity="0.6" />
    <rect x="5.2" y="10.1" width="36.6" height="2.9" rx="1.4" fill={grey500} opacity="0.6" />
    <rect x="5.2" y="15.2" width="30.3" height="2.9" rx="1.4" fill={grey500} opacity="0.6" />
  </g>
);

const ActionGridPreview: React.FC = () => (
  <svg viewBox="0 0 104 57.2" {...svgProps("slide-preview-actionGrid")}>
    <ActionBox x={0} y={0} />
    <ActionBox x={57} y={0} />
    <ActionBox x={0} y={32.6} />
    <ActionBox x={57} y={32.6} />
  </svg>
);

export const SLIDE_PREVIEW_COMPONENTS: Record<
  SlideThumbnailPreview,
  React.FC
> = {
  text: TextPreview,
  kpi: KpiPreview,
  barChart: BarChartPreview,
  trendChart: TrendChartPreview,
  actionGrid: ActionGridPreview,
};
